'use client'

import { useCallback, useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  RefundOfferAction,
  handleRefundOffer,
  getRefundOfferId,
  getRefundOfferEstimateDetails,
  transformRefundOfferIdDto,
  transformRefundOfferEstimateDto,
  partialRefundOffersClickEvent,
  transformEscrowRefundEstimateResp,
  getRefundOffersEstimate,
} from '@marketplace-web/complaints/complaints-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import GenericErrorModal from '../GenericErrorModal'
import PartialRefundOfferBuyerProtectionFeeDialog from '../PartialRefundOfferBuyerProtectionFeeDialog'
import PartialRefundOfferCurrencyConversionDialog from '../PartialRefundOfferCurrencyConversionDialog'
import AcceptPartialRefundOfferDialog from './AcceptPartialRefundOfferDialog'
import AcceptPartialRefundOfferDialogNew from './AcceptPartialRefundOfferDialogNew'
import PartialRefundOfferCurrencyConversionDialogNew from '../PartialRefundOfferCurrencyConversionDialogNew'

enum AcceptPartialRefundOfferActiveDialog {
  AcceptPartialRefundDialog,
  BuyerProtectionFeeDialog,
  CurrencyConversionDialog,
  ErrorDialog,
  None,
}

const AcceptPartialRefundOffer = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: ConversationActionProps) => {
  const { track } = useTracking()
  const isNewRefundSummaryEnabled = useFeatureSwitch('web_new_refund_summary')

  const { transaction } = useConversationContext()

  const transactionId = transaction?.id?.toString()
  const complaintId = transaction?.complaintId

  const [shouldFetchData, setShouldFetchData] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeDialog, setActiveDialog] = useState(AcceptPartialRefundOfferActiveDialog.None)

  const {
    fetch: fetchRefundOfferId,
    transformedData: refundOfferId,
    isLoading: isRefundOfferIdLoading,
  } = useFetch(getRefundOfferId, response => transformRefundOfferIdDto(response)?.refundOfferId)

  const {
    fetch: fetchRefundOfferEstimate,
    transformedData: refundOfferEstimate,
    isLoading: isRefundOfferEstimateLoading,
  } = useFetch(getRefundOfferEstimateDetails, response =>
    transformRefundOfferEstimateDto(response.refund_estimate),
  )

  const {
    fetch: fetchRefundEstimate,
    transformedData: refundEstimate,
    isLoading: isRefundEstimateLoading,
  } = useFetch(getRefundOffersEstimate, transformEscrowRefundEstimateResp)

  const fetchData = useCallback(async () => {
    if (
      activeDialog !== AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog ||
      !complaintId ||
      !shouldFetchData
    )
      return

    const { error, transformedData } = await fetchRefundOfferId(Number(complaintId))

    if (error || !transformedData) {
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

      return
    }

    const response = await fetchRefundOfferEstimate(transformedData)

    if (response.error || !response.transformedData)
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

    setShouldFetchData(false)
  }, [
    activeDialog,
    complaintId,
    fetchRefundOfferId,
    fetchRefundOfferEstimate,
    setActiveDialog,
    shouldFetchData,
  ])

  const fetchNewRefundEstimateData = useCallback(async () => {
    if (
      activeDialog !== AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog ||
      !complaintId ||
      !shouldFetchData
    )
      return

    const { error, transformedData } = await fetchRefundOfferId(Number(complaintId))

    if (error || !transformedData) {
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

      return
    }

    const response = await fetchRefundEstimate(transformedData)

    if (response.error || !response.transformedData)
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

    setShouldFetchData(false)
  }, [activeDialog, complaintId, fetchRefundEstimate, fetchRefundOfferId, shouldFetchData])

  useEffect(() => {
    if (isNewRefundSummaryEnabled) {
      fetchNewRefundEstimateData()

      return
    }

    fetchData()
  }, [fetchData, fetchNewRefundEstimateData, isNewRefundSummaryEnabled])

  const openDialog = (dialogType: AcceptPartialRefundOfferActiveDialog) => {
    setActiveDialog(dialogType)
  }

  const closeDialog = (dialogType = AcceptPartialRefundOfferActiveDialog.None) => {
    setActiveDialog(dialogType)
    setShouldFetchData(true)
  }

  const handleErrorDialogClose = () => {
    closeDialog()
    reloadPage()
  }

  const trackAcceptOfferClick = () => {
    if (!transactionId || !complaintId) return

    const targetDetails = JSON.stringify({
      partial_refund_amount: Number(refundOfferEstimate?.refundOffer?.amount),
      refund_offer_id: refundOfferId?.toString(),
    })

    track(
      partialRefundOffersClickEvent({
        target: 'partial_refund_offer_accept',
        transactionId,
        complaintId,
        targetDetails,
      }),
    )
  }

  const acceptPartialRefundOffer = async () => {
    if (isSubmitting || !refundOfferId) return

    setIsSubmitting(true)

    const response = await handleRefundOffer({
      refundOfferId,
      action: RefundOfferAction.Accept,
    })

    trackAcceptOfferClick()
    setIsSubmitting(false)

    if ('errors' in response) {
      openDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)
    } else {
      closeDialog()
    }
  }

  const renderPartialRefundOfferDialog = () => {
    if (isNewRefundSummaryEnabled)
      return (
        <AcceptPartialRefundOfferDialogNew
          refundEstimate={refundEstimate}
          isRefundEstimateLoading={isRefundEstimateLoading || isRefundOfferIdLoading}
          isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog}
          onCurrencyConversionClick={() =>
            openDialog(AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog)
          }
          onBuyerProtectionFeeInfoClick={() =>
            openDialog(AcceptPartialRefundOfferActiveDialog.BuyerProtectionFeeDialog)
          }
          onSubmit={acceptPartialRefundOffer}
          onClose={closeDialog}
        />
      )

    return (
      <AcceptPartialRefundOfferDialog
        refundOfferEstimate={refundOfferEstimate}
        isRefundOfferEstimateLoading={isRefundOfferEstimateLoading || isRefundOfferIdLoading}
        isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog}
        onCurrencyConversionClick={() =>
          openDialog(AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog)
        }
        onBuyerProtectionFeeInfoClick={() =>
          openDialog(AcceptPartialRefundOfferActiveDialog.BuyerProtectionFeeDialog)
        }
        onSubmit={acceptPartialRefundOffer}
        onClose={closeDialog}
      />
    )
  }

  const renderCurrencyConversionDialogue = () => {
    if (isNewRefundSummaryEnabled)
      return (
        refundEstimate && (
          <PartialRefundOfferCurrencyConversionDialogNew
            isOpenedInDialog
            refundEstimate={refundEstimate}
            isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog}
            onClose={() =>
              openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)
            }
          />
        )
      )

    return (
      refundOfferEstimate && (
        <PartialRefundOfferCurrencyConversionDialog
          isOpenedInDialog
          refundOffer={refundOfferEstimate.refundOffer}
          buyerProtectionFee={refundOfferEstimate.buyerProtectionFee}
          currencyConversion={refundOfferEstimate.currencyConversion}
          isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog}
          onClose={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
        />
      )
    )
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
        testId="accept-partial-refund-offer-conversation-action"
      />
      {renderPartialRefundOfferDialog()}
      <PartialRefundOfferBuyerProtectionFeeDialog
        isOpenedInDialog
        isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.BuyerProtectionFeeDialog}
        onClose={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
      />
      {renderCurrencyConversionDialogue()}
      <GenericErrorModal
        isShown={activeDialog === AcceptPartialRefundOfferActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default AcceptPartialRefundOffer
